import Request from "../../../services/request";

/**
 * Модульные схемы пользователей (e_module_schemes). Раскладку (layout) строят
 * на фронте-конструкторе, в админке — только список, название/code/SEO и
 * удаление, поэтому layout в список не запрашиваем.
 */

const API = "/dictionary/module-schemes";

const LIST_FIELDS = [
  "id",
  "name",
  "code",
  "type",
  "id_user",
  "id_object",
  "seo_title",
  "seo_description",
  "seo_keywords",
  "updated_at",
  // адрес готовой миниатюры схемы (SVG собирает и кэширует бэк)
  "_thumbnail_url",
];

function showError(commit, response) {
  commit(
    "change_notification_modal",
    { message: (response && response.message) || "Не удалось выполнить запрос", error: true },
    { root: true }
  );
}

// Запрос к слотам: возвращает массив записей слотов из data или null — тогда ошибка уже показана
async function slotsRequest(commit, send) {
  try {
    const response = await send();
    if (!response || response.isError) {
      showError(commit, response);
      return null;
    }
    return Array.isArray(response.data) ? response.data : [];
  } catch (e) {
    console.log(e);
    commit("change_notification_modal", e, { root: true });
    return null;
  }
}

export default {
  namespaced: true,
  state: {
    listEntries: [],
    loadingList: false,
    // base — базовые (правит только администратор), user — пользовательские
    typeFilter: "base",
  },
  mutations: {
    setListEntries(state, array) {
      state.listEntries = Array.isArray(array) ? array : Object.values(array || {});
    },
    changeLoadingList(state, value) {
      state.loadingList = value;
    },
    setTypeFilter(state, value) {
      state.typeFilter = value;
    },
    // точечно обновить строку списка (например, после смены объекта), не перечитывая список
    patchEntry(state, { id, patch }) {
      state.listEntries = state.listEntries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry));
    },
  },
  actions: {
    async getListEntries({ commit, state }) {
      commit("changeLoadingList", true);
      try {
        const query = Request.modifyQuery({ type: state.typeFilter }, LIST_FIELDS);
        const response = await Request.get(this.state.BASE_URL + `${API}${query}`);
        // 404 — схем ещё нет, это не ошибка для списка
        commit("setListEntries", response && !response.isError ? response.data : []);
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoadingList", false);
      }
    },
    // Что нарисовать для схемы: модули на своих местах и трубы на стыках. Геометрию считает бэк
    // (GET module-schemes/{id}/seams), здесь только запрос — копии расчёта в админке больше нет.
    // Без changeLoadingList — свой индикатор в диалоге. null — не загрузилось.
    async loadSeams({ commit }, id) {
      try {
        const response = await Request.get(this.state.BASE_URL + `${API}/${id}/seams`);
        if (!response || response.isError || !response.data) return null;
        return response.data;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
    // Объекты пользователя («мои объекты» — где он владелец или работник) для выбора объекта схемы:
    // [{ id, name, address }]. 404 — у пользователя их нет, это не ошибка.
    async loadObjects({ commit }, idUser) {
      if (!idUser) return [];
      try {
        const query = Request.modifyQuery({ id_user: idUser }, ["id", "name", "address"]);
        const response = await Request.get(this.state.BASE_URL + `/entity/objects${query}`);
        const rows = response && !response.isError ? response.data : [];
        return Array.isArray(rows) ? rows : Object.values(rows || {});
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return [];
      }
    },
    // Привязка схемы к объекту (null — снять). PUT схемы требует name и code, поэтому они передаются как
    // сохранены (не из полей окна, чтобы не записать недописанное). Бэк проверяет, что объект принадлежит
    // владельцу схемы; слоты схемы не переносятся — оборудование принадлежит объекту и узлу, схема просто
    // начинает показывать слоты комплектов нового объекта.
    // Возвращает true/false (ошибка уже показана).
    async updateObject({ commit, state }, { id, idObject }) {
      const saved = state.listEntries.find((entry) => entry.id === id);
      if (!saved) return false;
      try {
        const response = await Request.put(this.state.BASE_URL + `${API}/${id}`, {
          name: saved.name,
          code: saved.code,
          id_object: idObject || null,
        });
        if (!response || response.isError) {
          commit(
            "change_notification_modal",
            { message: (response && response.message) || "Не удалось сохранить объект схемы", error: true },
            { root: true }
          );
          return false;
        }
        commit("patchEntry", { id, patch: { id_object: idObject || null } });
        return true;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return false;
      }
    },
    // Вариант сборки узла на схеме (layout.nodes[].variant — подпапки «2», «3» в Elements чертежа узла): свежий
    // layout читается с бэка, меняется один узел и схема сохраняется (PUT требует name и code — берём сохранённые).
    // От варианта зависят картинка модуля и слоты схемы. Возвращает true/false (ошибка уже показана).
    async setModuleVariant({ commit, state }, { id, uid, variant }) {
      const saved = state.listEntries.find((entry) => entry.id === id);
      if (!saved) return false;
      const fail = (message) => {
        commit("change_notification_modal", { message, error: true }, { root: true });
        return false;
      };
      try {
        const current = await Request.get(
          this.state.BASE_URL + `${API}/${id}${Request.modifyQuery({}, ["id", "layout"])}`
        );
        const layout = current && !current.isError && current.data ? current.data.layout : null;
        const node = layout && (layout.nodes || []).find((n) => n.uid === uid);
        if (!node) return fail("Не удалось прочитать схему");

        node.variant = variant;
        const response = await Request.put(this.state.BASE_URL + `${API}/${id}`, {
          name: saved.name,
          code: saved.code,
          layout,
        });
        if (!response || response.isError) {
          return fail((response && response.message) || "Не удалось сохранить вариант сборки");
        }
        return true;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return false;
      }
    },
    // Слоты оборудования схемы: модули схемы (layout), экземпляры оборудования на их чертежах
    // (_detected_elements) и записи слотов из БД (позиция, количество, статус). Записи создаются
    // лениво, при первом выборе, поэтому у части экземпляров записи ещё нет.
    // Без changeLoadingList — свой индикатор у блока. null — не загрузилось.
    async loadSlots({ commit }, id) {
      try {
        const schemeResponse = await Request.get(
          this.state.BASE_URL +
            `${API}/${id}${Request.modifyQuery({}, ["id", "layout"])}`
        );
        if (!schemeResponse || schemeResponse.isError || !schemeResponse.data) return null;
        const layout = schemeResponse.data.layout || {};

        const ids = [...new Set((layout.nodes || []).map((n) => n.id_scheme_node))];
        let nodes = [];
        if (ids.length) {
          const query = Request.modifyQuery({ ids }, [
            "id",
            "code",
            "_detected_elements",
            "_detected_variants",
            "_functionalities",
          ]);
          const nodesResponse = await Request.get(
            this.state.BASE_URL + `/dictionary/scheme-nodes${query}`
          );
          nodes = nodesResponse && !nodesResponse.isError ? nodesResponse.data : [];
        }

        const slotsResponse = await Request.get(this.state.BASE_URL + `${API}/${id}/slots`);
        if (!slotsResponse || slotsResponse.isError) return null;
        const slots = Array.isArray(slotsResponse.data) ? slotsResponse.data : [];

        return { layout, nodes, slots };
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return null;
      }
    },
    // Создаёт записи слотов пачкой: items — [{ node_uid, id_scheme_node_element, element_instance,
    // quantity?, id_nomenclature? }]. Подпись и ограничения бэк берёт с узла сам. Адрес, который уже
    // есть, возвращается как есть. Возвращает записи слотов или null (ошибка уже показана).
    async createSlots({ commit }, { id, items }) {
      return slotsRequest(commit, () =>
        Request.post(this.state.BASE_URL + `${API}/${id}/slots`, { slots: items })
      );
    },
    // Массовое назначение: одна позиция и/или количество сразу нескольким слотам-наброскам.
    // changes — { id_nomenclature?: число|null, quantity?: число }. Всё или ничего: если среди
    // слотов есть купленный, бэк отвечает 409 и ничего не меняет.
    async updateSlots({ commit }, { id, ids, changes }) {
      return slotsRequest(commit, () =>
        Request.put(this.state.BASE_URL + `${API}/${id}/slots`, { ids, ...changes })
      );
    },
    // «Куплено»: фиксирует снимок позиции с ценой и замораживает слоты
    async purchaseSlots({ commit }, { id, ids }) {
      return slotsRequest(commit, () =>
        Request.post(this.state.BASE_URL + `${API}/${id}/slots/purchase`, { ids })
      );
    },
    // Удаляет слот-набросок (купленные и установленные бэк не отдаёт)
    async deleteSlot({ commit }, { id, idSlot }) {
      try {
        const response = await Request.delete(this.state.BASE_URL + `${API}/${id}/slots/${idSlot}`);
        if (!response || response.isError) {
          showError(commit, response);
          return false;
        }
        return true;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return false;
      }
    },
    // Подходящая номенклатура по условиям одного экземпляра оборудования узла
    async loadSlotCandidates({ commit }, { idNode, key }) {
      try {
        const response = await Request.get(
          this.state.BASE_URL +
            `/dictionary/scheme-node-nomenclature-rules/candidates?id_scheme_node=${idNode}&instances[]=${encodeURIComponent(key)}`
        );
        return response && !response.isError && response.data
          ? {
              items: response.data.items || [],
              problems: response.data.problems || [],
              rules: response.data.rules || [],
              family: response.data.family || null,
            }
          : { items: [], problems: [], rules: [], family: null };
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return { items: [], problems: [], rules: [], family: null };
      }
    },
    // Названия номенклатуры по id: «Семейство Название», как в списке подбора
    async loadNomenclatureNames({ commit }, ids) {
      if (!ids.length) return {};
      try {
        const query = Request.modifyQuery({ ids_nomenclatures: ids }, [
          "id",
          "name",
          "_name_family",
        ]);
        const response = await Request.get(this.state.BASE_URL + `/entity/nomenclature${query}`);
        const rows = response && !response.isError ? response.data : [];
        const names = {};
        (Array.isArray(rows) ? rows : Object.values(rows || {})).forEach((row) => {
          names[row.id] = `${row._name_family || ""} ${row.name}`.trim();
        });
        return names;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
        return {};
      }
    },
    async changeTypeFilter({ commit, dispatch }, value) {
      commit("setTypeFilter", value);
      await dispatch("getListEntries");
    },
    async updateEntry({ commit, dispatch }, payload) {
      commit("changeLoadingList", true);
      try {
        const response = await Request.put(
          this.state.BASE_URL + `${API}/${payload.id}`,
          {
            name: payload.name,
            code: payload.code,
            seo_title: payload.seo_title || null,
            seo_description: payload.seo_description || null,
            seo_keywords: payload.seo_keywords || null,
          }
        );
        if (response && !response.isError) {
          await dispatch("getListEntries");
        }
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoadingList", false);
      }
    },
    async deleteEntry({ commit, dispatch }, id) {
      commit("changeLoadingList", true);
      try {
        const response = await Request.delete(this.state.BASE_URL + `${API}/${id}`);
        if (response && !response.isError) {
          await dispatch("getListEntries");
        } else if (response) {
          // например, 409: в схеме есть купленное оборудование — без сообщения удаление выглядело бы «молчаливым»
          commit(
            "change_notification_modal",
            { message: response.message || "Не удалось удалить схему", error: true },
            { root: true }
          );
        }
        return response;
      } catch (e) {
        console.log(e);
        commit("change_notification_modal", e, { root: true });
      } finally {
        commit("changeLoadingList", false);
      }
    },
  },
};
